/**
 * CVManager Page
 * Manage CV/Resume files with upload, delete, and primary CV selection
 * MOBILE RESPONSIVE VERSION
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  Upload, 
  Trash2, 
  Star, 
  Download,
  Eye,
  Plus,
  Calendar,
  HardDrive,
  CheckCircle,
  AlertCircle,
  Loader,
  X,
  Menu
} from 'lucide-react';
import CVUpload from '../components/CVUpload';
import useCV from '../hooks/useCV';
import { toast } from 'react-hot-toast';

/**
 * CVManager Page Component
 * 
 * @returns {JSX.Element} CVManager component
 */
const CVManager = () => {
  const {
    cvs,
    primaryCV,
    loading,
    error,
    deleteCV,
    setPrimaryCV,
    getStats
  } = useCV();

  const [showUpload, setShowUpload] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cvToDelete, setCvToDelete] = useState(null);

  const stats = getStats();

  // Handle CV upload success
  const handleUploadSuccess = () => {
    setShowUpload(false);
    toast.success('CV uploaded successfully!');
  };

  // Show delete confirmation
  const showDeleteConfirmation = (cv) => {
    setCvToDelete(cv);
    setShowDeleteModal(true);
  };

  // Handle CV deletion
  const handleDeleteCV = async () => {
    if (!cvToDelete) return;

    try {
      setDeletingId(cvToDelete.id);
      await deleteCV(cvToDelete.id);
      setShowDeleteModal(false);
      setCvToDelete(null);
      toast.success('CV deleted successfully');
    } catch (error) {
      console.error('Error deleting CV:', error);
      toast.error('Failed to delete CV');
    } finally {
      setDeletingId(null);
    }
  };

  // Cancel deletion
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCvToDelete(null);
  };

  // Handle set primary CV
  const handleSetPrimary = async (cvId) => {
    try {
      await setPrimaryCV(cvId);
      toast.success('Primary CV updated');
    } catch (error) {
      console.error('Error setting primary CV:', error);
      toast.error('Failed to set primary CV');
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file icon
  const getFileIcon = (fileName) => {
    const extension = fileName.toLowerCase().split('.').pop();
    
    switch (extension) {
      case 'pdf':
        return <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />;
      case 'doc':
      case 'docx':
        return <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />;
      default:
        return <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" />;
    }
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    const date = timestamp.seconds 
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-green-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading your CVs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0 flex-1">
              <Link
                to="/career-opportunities"
                className="flex items-center text-gray-600 hover:text-gray-900 mr-2 sm:mr-4 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Back to Jobs</span>
                <span className="sm:hidden">Back</span>
              </Link>
              <div className="flex items-center min-w-0">
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-green-500 mr-2 flex-shrink-0" />
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                  CV Manager
                </h1>
              </div>
            </div>

            <button
              onClick={() => setShowUpload(!showUpload)}
              className="flex items-center px-3 py-2 sm:px-4 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
            >
              <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Upload CV</span>
              <span className="sm:hidden">Upload</span>
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 sm:py-8 max-w-7xl mx-auto">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <FileText className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{stats.total}</p>
                <p className="text-xs sm:text-sm text-gray-600">Total CVs</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <Star className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-500 mr-2 sm:mr-3 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                  {stats.hasPrimary ? '1' : '0'}
                </p>
                <p className="text-xs sm:text-sm text-gray-600">Primary CV</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <HardDrive className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500 mr-2 sm:mr-3 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                  {formatFileSize(stats.totalSize)}
                </p>
                <p className="text-xs sm:text-sm text-gray-600">Total Size</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
            <div className="flex items-center">
              <Upload className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500 mr-2 sm:mr-3 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                  {Object.keys(stats.formats).length || 0}
                </p>
                <p className="text-xs sm:text-sm text-gray-600">File Types</p>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        {showUpload && (
          <div className="bg-white rounded-lg shadow-sm border mb-6 sm:mb-8">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Upload New CV</h2>
                <button
                  onClick={() => setShowUpload(false)}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <CVUpload
                onUploadSuccess={handleUploadSuccess}
                onUploadError={(error) => console.error('Upload error:', error)}
              />
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-700 text-sm sm:text-base">{error}</span>
            </div>
          </div>
        )}

        {/* CV List */}
        {cvs.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No CVs uploaded yet</h3>
            <p className="text-gray-600 mb-6 px-4">
              Upload your first CV to start applying for jobs
            </p>
            <button
              onClick={() => setShowUpload(true)}
              className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
            >
              <Plus className="w-4 h-4 mr-2" />
              Upload Your First CV
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Your CVs ({cvs.length})
              </h2>
            </div>
            
            {cvs.map((cv) => (
              <div 
                key={cv.id} 
                className={`bg-white rounded-lg shadow-sm border p-4 sm:p-6 transition-all ${
                  cv.isPrimary ? 'ring-2 ring-green-500 border-green-200 bg-green-50' : 'hover:shadow-md'
                }`}
              >
                {/* Mobile Layout */}
                <div className="block sm:hidden">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      {getFileIcon(cv.fileName)}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-base font-medium text-gray-900 truncate">
                            {cv.fileName}
                          </h3>
                          {cv.isPrimary && (
                            <span className="inline-flex items-center bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0">
                              <Star className="w-2 h-2 mr-1" />
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="space-y-1 text-xs text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="w-3 h-3 mr-1" />
                            <span>Uploaded {formatDate(cv.uploadedAt)}</span>
                          </div>
                          <div className="flex items-center">
                            <HardDrive className="w-3 h-3 mr-1" />
                            <span>{formatFileSize(cv.fileSize)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile Actions */}
                  <div className="flex items-center justify-center space-x-1 pt-3 border-t border-gray-100">
                    <a
                      href={cv.downloadURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center py-2 px-3 text-blue-600 hover:bg-blue-50 rounded-md text-sm"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </a>
                    
                    <a
                      href={cv.downloadURL}
                      download={cv.fileName}
                      className="flex-1 flex items-center justify-center py-2 px-3 text-green-600 hover:bg-green-50 rounded-md text-sm"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </a>

                    {!cv.isPrimary && (
                      <button
                        onClick={() => handleSetPrimary(cv.id)}
                        className="flex-1 flex items-center justify-center py-2 px-3 text-yellow-600 hover:bg-yellow-50 rounded-md text-sm"
                      >
                        <Star className="w-4 h-4 mr-1" />
                        Primary
                      </button>
                    )}

                    <button
                      onClick={() => showDeleteConfirmation(cv)}
                      disabled={deletingId === cv.id}
                      className="flex-1 flex items-center justify-center py-2 px-3 text-red-600 hover:bg-red-50 rounded-md text-sm disabled:opacity-50"
                    >
                      {deletingId === cv.id ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden sm:block">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 min-w-0 flex-1">
                      {getFileIcon(cv.fileName)}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <h3 className="text-lg font-medium text-gray-900 truncate">
                            {cv.fileName}
                          </h3>
                          {cv.isPrimary && (
                            <span className="inline-flex items-center bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                              <Star className="w-3 h-3 mr-1" />
                              Primary
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            Uploaded {formatDate(cv.uploadedAt)}
                          </span>
                          <span className="flex items-center">
                            <HardDrive className="w-4 h-4 mr-1" />
                            {formatFileSize(cv.fileSize)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Desktop Actions */}
                    <div className="flex items-center space-x-2">
                      <a
                        href={cv.downloadURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-600 hover:text-blue-600 transition-colors rounded-md hover:bg-blue-50"
                        title="View CV"
                      >
                        <Eye className="w-5 h-5" />
                      </a>

                      <a
                        href={cv.downloadURL}
                        download={cv.fileName}
                        className="p-2 text-gray-600 hover:text-green-600 transition-colors rounded-md hover:bg-green-50"
                        title="Download CV"
                      >
                        <Download className="w-5 h-5" />
                      </a>

                      {!cv.isPrimary && (
                        <button
                          onClick={() => handleSetPrimary(cv.id)}
                          className="p-2 text-gray-600 hover:text-yellow-600 transition-colors rounded-md hover:bg-yellow-50"
                          title="Set as Primary CV"
                        >
                          <Star className="w-5 h-5" />
                        </button>
                      )}

                      <button
                        onClick={() => showDeleteConfirmation(cv)}
                        disabled={deletingId === cv.id}
                        className="p-2 text-gray-600 hover:text-red-600 transition-colors rounded-md hover:bg-red-50 disabled:opacity-50"
                        title="Delete CV"
                      >
                        {deletingId === cv.id ? (
                          <Loader className="w-5 h-5 animate-spin" />
                        ) : (
                          <Trash2 className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tips Section */}
        <div className="mt-6 sm:mt-8 bg-blue-50 p-4 sm:p-6 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-3">CV Management Tips:</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Keep multiple versions for different job types</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Set your most general CV as primary for quick applications</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Update your CVs regularly with new experiences</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Use PDF format for best compatibility with employers</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && cvToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto p-4">
          <div className="flex items-center justify-center min-h-screen">
            {/* Background overlay */}
            <div className="fixed inset-0 transition-opacity" onClick={cancelDelete}>
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            {/* Modal */}
            <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Delete CV
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete <strong>"{cvToDelete.fileName}"</strong>? 
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={handleDeleteCV}
                  disabled={deletingId === cvToDelete.id}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deletingId === cvToDelete.id ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete CV'
                  )}
                </button>
                <button
                  type="button"
                  onClick={cancelDelete}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CVManager;